import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { adminApi, docsApi, getToken } from '../api';
import Markdown from '../markdown/Markdown';
import { extractHeadings } from '../markdown/headings';
import TableOfContents from '../components/TableOfContents';
import { ArrowLeft, ArrowRight, Check, Copy, Edit } from '../components/Icons';

function pageMarkdown(page) {
  return `# ${page.title}\n\n${page.description ? `${page.description}\n\n` : ''}${page.content}`;
}

function CopyButton({ getText, label, className = '' }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={`docs-pill-btn ${className}`}
      onClick={async () => {
        const text = await getText();
        navigator.clipboard?.writeText(text).catch(() => {});
        setDone(true);
        setTimeout(() => setDone(false), 1800);
      }}
    >
      {done ? <Check size={14} /> : <Copy size={14} />}
      {done ? 'Copied' : label}
    </button>
  );
}

export default function DocPage() {
  const { slug } = useParams();
  const { product, version, basePath, products } = useOutletContext();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');
  const [pageId, setPageId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    docsApi.page(product, version, slug)
      .then((p) => { if (!cancelled) { setPage(p); setStatus('ready'); } })
      .catch((e) => { if (!cancelled) setStatus(e.status === 404 ? 'missing' : 'error'); });
    return () => { cancelled = true; };
  }, [product, version, slug]);

  useEffect(() => {
    const label = products?.find((p) => p.id === product)?.label || product;
    if (page) document.title = `${page.title} · ${label} ${version} · Veritage Docs`;
  }, [page, product, version, products]);

  // Scroll to the hash (or top) once the page has rendered.
  useEffect(() => {
    if (status !== 'ready') return;
    const id = decodeURIComponent(location.hash.slice(1));
    const el = id && document.getElementById(id);
    if (el) el.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
  }, [status, slug, location.hash]);

  // Admins get an edit shortcut (resolve the page id from the admin tree lazily).
  const isEditor = !!getToken();
  useEffect(() => {
    setPageId(null);
    if (!isEditor || !page) return;
    adminApi.tree().then((tree) => {
      const v = tree.find((p) => p.id === product)?.versions.find((x) => x.id === version);
      setPageId(v?.pages.find((p) => p.slug === slug)?.id || null);
    }).catch(() => {});
  }, [isEditor, page, product, version, slug]);

  const headings = useMemo(() => extractHeadings(page?.content), [page]);

  if (status === 'loading') {
    return (
      <>
        <main className="docs-main">
          <div className="docs-skel" style={{ width: 120, height: 12, marginBottom: 20 }} />
          <div className="docs-skel" style={{ width: '60%', height: 44, marginBottom: 20 }} />
          <div className="docs-skel" style={{ width: '90%', height: 16, marginBottom: 10 }} />
          <div className="docs-skel" style={{ width: '75%', height: 16, marginBottom: 40 }} />
          <div className="docs-skel" style={{ width: '100%', height: 180 }} />
        </main>
        <TableOfContents headings={[]} />
      </>
    );
  }

  if (status !== 'ready') {
    return (
      <>
        <main className="docs-main docs-missing">
          <div className="vt-eyebrow">{status === 'missing' ? '404' : 'Error'}</div>
          <h1 className="docs-title">{status === 'missing' ? 'Page not found' : 'Something went wrong'}</h1>
          <p className="docs-lead">
            {status === 'missing'
              ? 'This page may have moved or been unpublished. Try search, or start from the overview.'
              : 'We could not load this page. Please try again in a moment.'}
          </p>
          <Link to={basePath} className="vt-btn vt-btn--sm">Back to docs <span className="vt-btn-arrow"><ArrowRight size={14} /></span></Link>
        </main>
        <TableOfContents headings={[]} />
      </>
    );
  }

  return (
    <>
      <main className="docs-main" key={slug}>
        <header className="docs-header">
          <div className="vt-eyebrow docs-header-eyebrow">{page.section}</div>
          <h1 className="docs-title">{page.title}</h1>
          {page.description && (
            <div className="docs-lead"><Markdown content={page.description} basePath={basePath} /></div>
          )}
          <div className="docs-header-actions">
            <CopyButton label="Copy page" getText={() => pageMarkdown(page)} />
            <CopyButton label="Copy all docs for LLM" getText={() => docsApi.exportText(product, version)} />
            {pageId && (
              <Link className="docs-pill-btn docs-pill-btn--accent" to={`/admin/pages/${pageId}`}>
                <Edit size={14} /> Edit page
              </Link>
            )}
          </div>
        </header>

        <Markdown content={page.content} basePath={basePath} />

        <nav className="docs-pager" aria-label="Pagination">
          {page.prev ? (
            <Link className="docs-pager-card" to={`${basePath}/${page.prev.slug}`}>
              <span className="vt-eyebrow"><ArrowLeft size={12} /> Previous</span>
              <span className="docs-pager-title">{page.prev.title}</span>
            </Link>
          ) : <span />}
          {page.next && (
            <Link className="docs-pager-card is-next" to={`${basePath}/${page.next.slug}`}>
              <span className="vt-eyebrow">Next <ArrowRight size={12} /></span>
              <span className="docs-pager-title">{page.next.title}</span>
            </Link>
          )}
        </nav>

        {page.publishedAt && (
          <p className="docs-updated">
            Last updated {new Date(page.publishedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        )}
      </main>
      <TableOfContents headings={headings}>
        <div className="docs-toc-help">
          <div className="vt-eyebrow">Need a hand?</div>
          <p>Talk to the team about your integration.</p>
          <a className="vt-btn vt-btn--sm" href="https://veritage.io/contact/">Book a call <span className="vt-btn-arrow"><ArrowRight size={12} /></span></a>
        </div>
      </TableOfContents>
    </>
  );
}
